"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataCollection = exports.DATA_COLLECTION_HISTORY_TYPE = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const rxjs_1 = require("rxjs");
var DATA_COLLECTION_HISTORY_TYPE;
(function (DATA_COLLECTION_HISTORY_TYPE) {
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Add"] = 0] = "Add";
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Remove"] = 1] = "Remove";
    DATA_COLLECTION_HISTORY_TYPE[DATA_COLLECTION_HISTORY_TYPE["Edit"] = 2] = "Edit";
})(DATA_COLLECTION_HISTORY_TYPE = exports.DATA_COLLECTION_HISTORY_TYPE || (exports.DATA_COLLECTION_HISTORY_TYPE = {}));
const defaultItemsComparator = (one, two) => (0, fast_deep_equal_1.default)(one, two);
class DataCollection {
    constructor(initial, idGetter, itemsComparator = defaultItemsComparator) {
        this.idGetter = idGetter;
        this.itemsComparator = itemsComparator;
        this.history = [];
        this.items = new Map(initial.map((item) => [idGetter(item), item]));
        this.onCollectionChangedSubj = new rxjs_1.Subject();
    }
    applyHistoryRecord(record) {
        this.history.push(record);
        this.onCollectionChangedSubj.next(record);
    }
    asObservable() {
        return this.onCollectionChangedSubj.asObservable();
    }
    reset(initial = []) {
        this.history = [];
        this.items = new Map(initial.map((item) => [this.idGetter(item), item]));
    }
    getItems() {
        return Array.from(this.items.values());
    }
    safeAddItem(added) {
        const id = this.idGetter(added);
        if (this.items.has(id))
            return new Error(`item with id '${id}' has already been added`);
        this.items.set(id, added);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Add, id, added });
    }
    addItem(added) {
        const result = this.safeAddItem(added);
        if (result !== undefined)
            throw result;
    }
    tryAddItem(added) {
        const result = this.safeAddItem(added);
        return result === undefined;
    }
    safeRemoveItem(id) {
        const removed = this.items.get(id);
        if (removed === undefined)
            return new Error(`item with id '${id}' is not existed`);
        this.items.delete(id);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Remove, id, removed });
    }
    removeItem(id) {
        const result = this.safeRemoveItem(id);
        if (result !== undefined)
            throw result;
    }
    tryRemoveItem(id) {
        const result = this.safeRemoveItem(id);
        return result === undefined;
    }
    safeEditItem(id, newItem) {
        const newItemId = this.idGetter(newItem);
        if (newItemId !== id)
            return new Error(`id('${id}') and newItem's id('${newItemId}') are not equal`);
        const oldItem = this.items.get(id);
        if (oldItem === undefined)
            return new Error(`item with id \'${id}\' is not existed`);
        this.items.set(id, newItem);
        this.applyHistoryRecord({ type: DATA_COLLECTION_HISTORY_TYPE.Edit, id, oldItem, newItem });
    }
    editItem(id, newItem) {
        const result = this.safeEditItem(id, newItem);
        if (result !== undefined)
            throw result;
    }
    tryEditItem(id, newItem) {
        const result = this.safeEditItem(id, newItem);
        return result === undefined;
    }
    addOrEditItem(newItem) {
        const id = this.idGetter(newItem);
        const oldItem = this.items.get(id);
        if (oldItem === undefined)
            this.addItem(newItem);
        else
            this.editItem(id, newItem);
    }
    getDiff() {
        const addedItems = new Map();
        const removedItems = new Map();
        const editedItems = new Map();
        this.history.forEach((record) => {
            switch (record.type) {
                case DATA_COLLECTION_HISTORY_TYPE.Add:
                    {
                        // don't need check added. item cannot be added if key already exist
                        // don't need check edited. item cannot be added if key already exist
                        const removed = removedItems.get(record.id);
                        if (removed !== undefined) {
                            const areEqual = this.itemsComparator(record.added, removed);
                            if (!areEqual) {
                                editedItems.set(record.id, { old: removed, new: record.added });
                            }
                            removedItems.delete(record.id);
                        }
                        else
                            addedItems.set(record.id, record.added);
                    }
                    break;
                case DATA_COLLECTION_HISTORY_TYPE.Remove:
                    {
                        // don't need check removed. item cannot be removed if key hasn't exist
                        const isAdded = addedItems.delete(record.id);
                        if (!isAdded) {
                            const edited = editedItems.get(record.id);
                            if (edited !== undefined) {
                                editedItems.delete(record.id);
                                removedItems.set(record.id, edited.old);
                            }
                            else
                                removedItems.set(record.id, record.removed);
                        }
                    }
                    break;
                case DATA_COLLECTION_HISTORY_TYPE.Edit:
                    {
                        // don't need check removed. item cannot be edited if key hasn't exist
                        const added = addedItems.get(record.id);
                        const edited = editedItems.get(record.id);
                        if (added !== undefined) {
                            addedItems.set(record.id, record.newItem);
                        }
                        else if (edited !== undefined) {
                            editedItems.set(record.id, { old: edited.old, new: record.newItem });
                        }
                        else
                            editedItems.set(record.id, {
                                old: record.oldItem,
                                new: record.newItem
                            });
                    }
                    break;
            }
        });
        return {
            added: Array.from(addedItems.values()),
            edited: Array.from(editedItems.values()),
            removed: Array.from(removedItems.values())
        };
    }
    isChanged() {
        const { added, edited, removed } = this.getDiff();
        return added.length === 0 && edited.length === 0 && removed.length === 0;
    }
}
exports.DataCollection = DataCollection;
//# sourceMappingURL=DataCollection.js.map